import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, ShieldAlert, Zap, Lock, Eye, Activity, Terminal, Shield, Cpu } from 'lucide-react';

interface AIFirewallDashboardProps {
  lastScan?: string;
  threatLevel?: 'low' | 'medium' | 'high';
}

export function AIFirewallDashboard({ lastScan = 'Just now', threatLevel = 'low' }: AIFirewallDashboardProps) {
  const [logs, setLogs] = React.useState<{ id: string, msg: string, time: string, type: 'info' | 'warn' | 'block' | 'attack' }[]>([
    { id: '1', msg: 'System integrity verified. AI Shield Active.', time: '10:42:01', type: 'info' },
    { id: '2', msg: 'Prompt injection attempt blocked (Lagos Island).', time: '10:45:15', type: 'block' },
    { id: '3', msg: 'PII leakage prevented in Traffic report.', time: '10:50:22', type: 'warn' },
    { id: '4', msg: 'Adversarial pattern detected: Jailbreak attempt.', time: '10:55:10', type: 'attack' },
  ]);

  const [scanPulse, setScanPulse] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      const messages = [
        { msg: 'Scanning neighborhood pulses...', type: 'info' as const },
        { msg: 'Evaluating trust scores...', type: 'info' as const },
        { msg: 'Masking sensitive identifiers...', type: 'info' as const },
        { msg: 'Neutralizing malicious script tags...', type: 'block' as const },
        { msg: 'Adversarial attack surface scan: CLEAR', type: 'info' as const },
        { msg: 'Coordinated misinformation pattern blocked.', type: 'block' as const },
        { msg: 'Detected obfuscated jailbreak attempt.', type: 'attack' as const },
      ];
      const randomMsg = messages[Math.floor(Math.random() * messages.length)];
      setLogs(prev => [{ id: Date.now().toString(), ...randomMsg, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 5));
      setScanPulse(p => (p + 1) % 100);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900 rounded-[32px] p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10">
          <Cpu className="w-32 h-32 animate-pulse" />
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-green-500/20 rounded-2xl border border-green-500/30">
              <Shield className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-black italic uppercase tracking-tighter">Sentlo AI Firewall</h2>
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Enterprise-Grade Community Protection</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <p className="text-[8px] font-black text-white/40 uppercase tracking-widest mb-1">Defense Status</p>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-black uppercase tracking-tight">Active & Vigilant</span>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <p className="text-[8px] font-black text-white/40 uppercase tracking-widest mb-1">Threat Level</p>
              <span className={`text-sm font-black uppercase tracking-tight ${threatLevel === 'low' ? 'text-green-400' : 'text-amber-400'}`}>
                {threatLevel.toUpperCase()} (SAFE)
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-zinc-50 rounded-[40px] p-6 border border-zinc-100 relative overflow-hidden group">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white border border-zinc-100 flex items-center justify-center text-zinc-900 shadow-sm relative overflow-hidden">
              <Activity className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <motion.div 
                className="absolute inset-0 bg-green-500/10"
                animate={{ y: ['-100%', '100%'] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              />
            </div>
            <div>
              <h3 className="text-sm font-black italic uppercase tracking-tighter leading-none mb-1">Continuous Monitoring</h3>
              <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest">Active Scanning</p>
            </div>
          </div>
          <div className="px-3 py-1 bg-green-500/20 rounded-full flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            <span className="text-[8px] font-black text-green-700 uppercase tracking-widest leading-none">Safe</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="flex justify-between items-center text-[8px] font-black uppercase text-zinc-400 mb-1 px-1">
              <span>Adversarial Pattern Detection</span>
              <span className="text-zinc-900">99.2%</span>
            </div>
            <div className="h-1.5 w-full bg-zinc-200 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-zinc-900"
                initial={{ width: 0 }}
                animate={{ width: '99.2%' }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between items-center text-[8px] font-black uppercase text-zinc-400 mb-1 px-1">
              <span>Attack Surface Scan</span>
              <span className="text-zinc-900">Live</span>
            </div>
            <div className="h-1.5 w-full bg-zinc-200 rounded-full overflow-hidden flex gap-0.5">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <motion.div 
                  key={i}
                  className="flex-1 bg-green-400 rounded-full"
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{ duration: 1, repeat: Infinity, delay: i * 0.125 }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-200/50 flex items-center justify-between">
          <div className="flex gap-4">
            <div className="flex flex-col">
              <span className="text-[8px] font-black text-zinc-400 uppercase tracking-widest">Jailbreak Protection</span>
              <span className="text-[10px] font-bold text-zinc-900">EVALUATED</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[8px] font-black text-zinc-400 uppercase tracking-widest">Prompt Leak Detection</span>
              <span className="text-[10px] font-bold text-zinc-900">ZERO TRUST</span>
            </div>
          </div>
          <div className="w-8 h-8 rounded-full bg-zinc-900 flex items-center justify-center text-white">
            <Lock className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2">
            <Terminal className="w-3.5 h-3.5 text-zinc-400" />
            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Live Firewall Logs</span>
          </div>
          <span className="text-[8px] font-black text-green-600 bg-green-50 px-2 py-0.5 rounded-full uppercase">Real-time</span>
        </div>

        <div className="bg-zinc-50 rounded-3xl border border-zinc-100 overflow-hidden">
          <div className="p-2 space-y-1">
            <AnimatePresence initial={false}>
              {logs.map((log) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex items-start gap-3 p-3 bg-white border border-zinc-50 rounded-2xl shadow-sm"
                >
                  <div className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                    log.type === 'block' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' :
                    log.type === 'attack' ? 'bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.5)]' :
                    log.type === 'warn' ? 'bg-amber-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-[11px] font-bold leading-tight ${
                      log.type === 'block' ? 'text-red-700' : 
                      log.type === 'attack' ? 'text-purple-700 font-black italic uppercase tracking-tighter' :
                      'text-zinc-700'
                    }`}>
                      {log.msg}
                    </p>
                    <p className="text-[9px] text-zinc-400 font-medium uppercase mt-0.5">{log.time}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Latency', value: '14ms', icon: Activity },
          { label: 'Interception', value: '4.2k', icon: Zap },
          { label: 'Redaction', value: '98%', icon: Eye },
        ].map((stat, idx) => (
          <div key={idx} className="bg-white border border-zinc-100 p-4 rounded-3xl flex flex-col items-center text-center gap-1">
            <stat.icon className="w-4 h-4 text-zinc-400 mb-1" />
            <span className="text-xs font-black text-zinc-900">{stat.value}</span>
            <span className="text-[8px] font-black text-zinc-400 uppercase tracking-widest">{stat.label}</span>
          </div>
        ))}
      </div>

      <div className="p-5 bg-blue-50 rounded-[32px] border border-blue-100 flex items-start gap-4">
        <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center text-blue-600 shadow-sm shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="space-y-1">
          <p className="text-xs font-black text-blue-900 uppercase tracking-tight">AI Firewall Policy</p>
          <p className="text-[10px] text-blue-700 leading-relaxed font-medium">
            The firewall automatically intercepts and neutralizes threats like prompt injection, social engineering, and coordinated misinformation campaigns before they reach the community feed.
          </p>
        </div>
      </div>
    </div>
  );
}
